import { useNavigate, useLocation } from "react-router-dom";
import api, { resolveAssetUrl } from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import {
  Box, Button, Chip, CircularProgress,
  Collapse, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle,
  IconButton, InputAdornment,
  Stack, TextField, Typography,
  FormControl, Select, MenuItem, InputLabel,
  Table, TableBody, TableCell,
  TableHead, TableRow, Paper, Tooltip, Avatar,
  TableContainer,
} from "@mui/material";
import { parseProductListResponse } from "utils/parseListResponse";
import { useCurrency } from "contexts/CurrencyContext";
import { formatCurrency } from "utils/currency";

import AddIcon from "@mui/icons-material/Add";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import TableStatusChip from "components/table/TableStatusChip";
import { timeChipSx, vegIndicatorSx, vegIndicatorDotSx, highlightChipSx } from "components/table/tableChipStyles";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "contexts/ToastContext";
import ProductFilteredByStoreForm from "pages/master/ProductFilteredByStoreForm";

const getImgUrl = (p) => {
  if (p?.image_url) return p.image_url;
  const d = p?.doc || p?.Doc;
  if (d?.imageUrl) return d.imageUrl;
  if (d?.attachment_url) return resolveAssetUrl(d.attachment_url);
  return null;
};

function VariantRows({ variants }) {
  if (!variants?.length) return null;
  return (
    <TableRow>
      <TableCell colSpan={13} sx={{ py: 0, bgcolor: "grey.50", borderBottom: "2px solid", borderColor: "primary.lighter" }}>
        <Collapse in timeout="auto" unmountOnExit>
          <Box sx={{ px: 5, py: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              textTransform="uppercase" letterSpacing={0.5} mb={1} display="block">
              Variants ({variants.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  {["#", "Name", `Price (${currency.symbol})`, "Description", "Sequence"].map((h) => (
                    <TableCell key={h}
                      sx={{
                        fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase",
                        color: "text.secondary", py: 0.8, letterSpacing: 0.4
                      }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.map((v, i) => (
                  <TableRow key={v.id} hover>
                    <TableCell sx={{ py: 0.8, color: "text.disabled", width: 36 }}>{i + 1}</TableCell>
                    <TableCell sx={{ py: 0.8, fontWeight: 600 }}>{v.name}</TableCell>
                    <TableCell sx={{ py: 0.8, color: "primary.main", fontWeight: 700 }}>
                      {formatCurrency(v.price, currency.code, currency.symbol)}
                    </TableCell>
                    <TableCell sx={{ py: 0.8, color: "text.secondary", maxWidth: 260 }}>
                      {v.description || "—"}
                    </TableCell>
                    <TableCell sx={{ py: 0.8, color: "text.disabled" }}>{v.sequence ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

function MaxCountCell({ limit }) {
  if (!limit) {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }

  const { maximum_count, ordered_count, remaining_count, is_available } = limit;
  const isUnlimited = maximum_count === 0;

  if (isUnlimited) {
    return <Chip label="Unlimited" size="small" sx={timeChipSx} />;
  }

  const pct = Math.round((ordered_count / maximum_count) * 100);
  const color = !is_available ? "error" : pct >= 80 ? "warning" : "success";

  return (
    <Box sx={{ minWidth: 90 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} mb={0.4}>
        <Typography variant="caption" fontWeight={700} color={`${color}.main`}>
          {remaining_count ?? 0}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          / {maximum_count}
        </Typography>
      </Stack>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: "grey.200", overflow: "hidden" }}>
        <Box sx={{
          height: "100%", borderRadius: 2,
          width: `${Math.min(pct, 100)}%`,
          bgcolor: !is_available ? "error.main" : pct >= 80 ? "warning.main" : "success.main",
          transition: "width 0.3s",
        }} />
      </Box>
      {!is_available && (
        <Typography variant="caption" color="error.main"
          display="block" textAlign="center" mt={0.3}>
          Sold out
        </Typography>
      )}
    </Box>
  );
}

function ProductRow({ p, idx, onEdit, onDelete, onToggleStatus, togglingId }) {
  const { currency } = useCurrency();
  const [expanded, setExpanded] = useState(false);
  const hasVariants = p.ProductVariants?.length > 0;
  const image = getImgUrl(p);

  return (
    <>
      <TableRow hover sx={{
        verticalAlign: "middle",
        "& > *": { borderBottom: expanded ? "unset" : undefined }
      }}>

        {/* expand toggle */}
        <TableCell sx={{ width: 44, py: 1, px: 1 }}>
          {hasVariants && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded
                ? <KeyboardArrowUpIcon fontSize="small" />
                : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>

        {/* # */}
        <TableCell sx={{ color: "text.disabled", fontSize: 13, width: 44 }}>
          {idx + 1}
        </TableCell>

        {/* image */}
        <TableCell sx={{ width: 64 }}>
          <Avatar src={image || undefined} variant="rounded"
            sx={{
              width: 48, height: 48, bgcolor: "primary.lighter",
              border: "1px solid", borderColor: "divider"
            }}>
            {!image && <RestaurantMenuIcon sx={{ fontSize: 20 }} />}
          </Avatar>
        </TableCell>

        {/* name + arabic */}
        <TableCell sx={{ minWidth: 180 }}>
          <Stack direction="row" alignItems="flex-start" gap={0.75}>
            {p.is_veg !== null && p.is_veg !== undefined && (
              <Tooltip title={p.is_veg ? "Veg" : "Non-Veg"}>
                <Box sx={vegIndicatorSx(p.is_veg)}>
                  <Box sx={vegIndicatorDotSx(p.is_veg)} />
                </Box>
              </Tooltip>
            )}
            <Box>
              <Typography fontWeight={600} variant="body2">{p.name}</Typography>
              {p.name_ar && (
                <Typography variant="caption" color="text.secondary"
                  display="block" sx={{ direction: "rtl" }}>
                  {p.name_ar}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>

        {/* description */}
        <TableCell sx={{ minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{
              display: "-webkit-box", WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical", overflow: "hidden", maxWidth: 200
            }}>
            {p.description || "—"}
          </Typography>
        </TableCell>

        {/* category */}
        <TableCell sx={{ minWidth: 130 }}>
          <Typography variant="body2" color="text.secondary">
            {p.ProductCategory?.name_en || "—"}
          </Typography>
        </TableCell>

        {/* price */}
        <TableCell align="right" sx={{ minWidth: 90 }}>
          <Typography fontWeight={700} color="primary.main" variant="body2">
            {formatCurrency(p.price, currency.code, currency.symbol)}
          </Typography>
        </TableCell>

        {/* variants count */}
        <TableCell align="center" sx={{ minWidth: 90 }}>
          {hasVariants ? (
            <Chip
              label={`${p.ProductVariants.length} variant${p.ProductVariants.length > 1 ? "s" : ""}`}
              size="small" color="secondary" variant="outlined"
              onClick={() => setExpanded(!expanded)}
              sx={{ cursor: "pointer" }}
            />
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>

        {/* highlight */}
        <TableCell align="center" sx={{ minWidth: 90 }}>
          <Chip
            label={p.highlight ? "Yes" : "No"}
            size="small"
            sx={highlightChipSx(p.highlight)}
          />
        </TableCell>

        {/* sequence */}
        <TableCell align="center" sx={{ minWidth: 60 }}>
          <Typography variant="body2" color="text.secondary">{p.sequence ?? "—"}</Typography>
        </TableCell>

        {/*  max count */}
        <TableCell align="center" sx={{ minWidth: 110 }}>
          <MaxCountCell limit={p.limit} />
        </TableCell>

        {/* status toggle */}
        <TableCell align="center" sx={{ minWidth: 95 }}>
          <TableStatusChip
            active={!!p.status}
            loading={togglingId === p.id}
            onClick={() => togglingId !== p.id && onToggleStatus(p)}
            sx={{ minWidth: 90 }}
          />
        </TableCell>

        {/* actions */}
        <TableCell align="center" sx={{ minWidth: 90 }}>
          <TableActions>
            <TableActionButton action="edit" onClick={() => onEdit(p)} />
            <TableActionButton action="delete" onClick={() => onDelete(p)} />
          </TableActions>
        </TableCell>

      </TableRow>

      {expanded && <VariantRows variants={p.ProductVariants} />}
    </>
  );
}

const PAGE_LIMIT = 8;

export default function ProductList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, showApiError } = useToast();

  const params = new URLSearchParams(location.search);
  const storeId = params.get("store_id") || "";
  const categoryId = params.get("category_id") || "";
  const categoryName = params.get("category_name") || "Products";
  const storeNameParam = params.get("store_name") || "";

  const [storeName, setStoreName] = useState(storeNameParam);
  const [categories, setCategories] = useState([]);
  const [filterCategoryId, setFilterCategoryId] = useState(categoryId);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [storeStatus, setStoreStatus] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [delDialog, setDelDialog] = useState({ open: false, id: null, name: "" });
  const [deleting, setDeleting] = useState(false);
  const [formModal, setFormModal] = useState({ open: false, id: null });
  const nextPageRef = useRef(1);
  const scrollRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreRef = useRef(() => {});

  useEffect(() => {
    if (!storeId) return;
    api.get(`/store/${storeId}`)
      .then((res) => {
        const d = res.data?.data;
        setStoreName(d?.title_en || d?.name_en || "Store");
        setStoreStatus(d?.status ?? true);
      })
      .catch(() => {
        setStoreName("Store");
        setStoreStatus(true);
      });
  }, [storeId]);

  useEffect(() => {
    setFilterCategoryId(categoryId);
  }, [categoryId]);

  useEffect(() => {
    if (!storeId) {
      setCategories([]);
      return;
    }
    api
      .get("/product/category/list", { params: { store_id: storeId } })
      .then((res) => setCategories(res.data?.data || []))
      .catch(() => setCategories([]));
  }, [storeId]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const buildListParams = useCallback(
    (pageNum) => {
      const params = {
        store_id: storeId,
        page: pageNum,
        limit: PAGE_LIMIT
      };
      if (filterCategoryId) params.category_id = filterCategoryId;
      if (searchDebounced) {
        params.search = searchDebounced;
        params.q = searchDebounced;
      }
      return params;
    },
    [storeId, filterCategoryId, searchDebounced]
  );

  const fetchProductsPage = useCallback(
    async (pageNum) => {
      const res = await api.get("/product/list", { params: buildListParams(pageNum) });
      return parseProductListResponse(res);
    },
    [buildListParams]
  );

  const loadInitial = useCallback(async () => {
    if (!storeId || !filterCategoryId) {
      setLoading(false);
      setHasFetched(true);
      setProducts([]);
      setTotal(0);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setHasMore(true);
    nextPageRef.current = 1;
    try {
      const result = await fetchProductsPage(1);
      setProducts(result.products);
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = 2;
    } catch {
      setProducts([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [storeId, filterCategoryId, fetchProductsPage]);

  const displayCategoryName =
    categories.find((c) => c.id === filterCategoryId)?.name_en ||
    decodeURIComponent(categoryName || "Products");

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (!storeId || !filterCategoryId || loading || loadingMore || !hasMore || loadMoreInFlightRef.current) {
      return;
    }

    const pageNum = nextPageRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const result = await fetchProductsPage(pageNum);
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const added = result.products.filter((p) => !seen.has(p.id));
        return [...prev, ...added];
      });
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = pageNum + 1;
    } catch (err) {
      showApiError(err, "Failed to load more products");
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [storeId, filterCategoryId, loading, loadingMore, hasMore, fetchProductsPage, showApiError]);

  loadMoreRef.current = loadMore;

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root, rootMargin: "40px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  const handleToggleStatus = async (p) => {
    setTogglingId(p.id);
    const nextStatus = !p.status;
    try {
      await api.put(`/product/manage/status/${p.id}`, { status: nextStatus });
      setProducts((prev) =>
        prev.map((r) => r.id === p.id ? { ...r, status: nextStatus } : r)
      );
    } catch (err) {
      showApiError(err, "Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/product/delete/${delDialog.id}`);
      setDelDialog({ open: false, id: null, name: "" });
      loadInitial();
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const openProductForm = (p = null) => {
    setFormModal({ open: true, id: p?.id ?? null });
  };

  const COLS = [
    { label: "", align: "center" },
    { label: "#", align: "center" },
    { label: "Image", align: "left" },
    { label: "Name", align: "left" },
    { label: "Description", align: "left" },
    { label: "Category", align: "left" },
    { label: "Price", align: "right" },
    { label: "Variants", align: "center" },
    { label: "Highlight", align: "center" },
    { label: "Seq", align: "center" },
    { label: "Max Count", align: "center" },
    { label: "Status", align: "center" },
    { label: "Actions", align: "center" },
  ];

  return (
    <Box>
      <PageHeader
        variant="nested"
        onBack={() => navigate(-1)}
        backTitle="Back to categories"
        title={displayCategoryName}
        subtitle={
          storeName
            ? `Store: ${decodeURIComponent(storeName)} — manage products in this category.`
            : "Manage products in this category."
        }
        count={total}
        titleExtra={
          !storeStatus ? (
            <Chip label="Store Inactive" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
          ) : null
        }
        actionLabel="Add Product"
        actionIcon={AddIcon}
        onAction={() => openProductForm()}
      />

      {/* filters */}
      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!storeId}>
          <InputLabel id="product-category-filter">Category</InputLabel>
          <Select
            labelId="product-category-filter"
            value={filterCategoryId}
            label="Category"
            onChange={(e) => setFilterCategoryId(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <CategoryIcon fontSize="small" color="action" />
              </InputAdornment>
            }
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name_en || c.name_ar || "—"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search by name, description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 420, flex: 1 }}
        />
      </Box>

      {/* table */}
      <MainCard contentSX={{ p: 0 }}>
        {!storeId || !filterCategoryId ? (
          <Box sx={{ textAlign: "center", p: 6, color: "text.secondary" }}>
            <Typography variant="body1">Select a store and category to view products.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              ref={scrollRef}
              component={Paper}
              elevation={0}
              sx={{
                display: "block",
                maxHeight: { xs: "55vh", md: 520 },
                minHeight: 360,
                overflowX: "auto",
                overflowY: "scroll",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(31, 91, 64, 0.45) rgba(31, 91, 64, 0.08)",
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": { width: 10, height: 10 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(31, 91, 64, 0.4)",
                  borderRadius: 8,
                  border: "2px solid rgba(31, 91, 64, 0.06)"
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(31, 91, 64, 0.08)",
                  borderRadius: 8
                }
              }}
            >
              <Table stickyHeader sx={{ minWidth: 1300 }}>
                <TableHead>
                  <TableRow>
                    {COLS.map((col, i) => (
                      <TableCell
                        key={i}
                        align={col.align}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          bgcolor: "grey.50",
                          whiteSpace: "nowrap",
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          py: 1.5
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={COLS.length} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={36} />
                      </TableCell>
                    </TableRow>
                  ) : products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLS.length} align="center" sx={{ py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                          {hasFetched
                            ? search
                              ? `No products match "${search}"`
                              : "No products found"
                            : "Loading products…"}
                        </Typography>
                        {hasFetched && !search && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mt: 2 }}
                            onClick={() => openProductForm()}
                          >
                            Add First Product
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((p, idx) => (
                      <ProductRow
                        key={p.id}
                        p={p}
                        idx={idx}
                        onEdit={openProductForm}
                        onDelete={(prod) =>
                          setDelDialog({ open: true, id: prod.id, name: prod.name })
                        }
                        onToggleStatus={handleToggleStatus}
                        togglingId={togglingId}
                      />
                    ))
                  )}
                  {!loading && products.length > 0 && (
                    <TableRow>
                      <TableCell
                        ref={loadMoreSentinelRef}
                        colSpan={COLS.length}
                        align="center"
                        sx={{ py: 1.5, border: 0 }}
                      >
                        {loadingMore ? (
                          <CircularProgress size={24} />
                        ) : !hasMore ? (
                          <Typography variant="caption" color="text.secondary">
                            All {total} products loaded
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Scroll for more
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </MainCard>

      <ProductFilteredByStoreForm
        open={formModal.open}
        productId={formModal.id}
        storeId={storeId}
        storeName={decodeURIComponent(storeName || "")}
        initialCategoryId={filterCategoryId}
        onSave={loadInitial}
        onClose={() => setFormModal({ open: false, id: null })}
      />

      {/* delete dialog */}
      <Dialog
        open={delDialog.open}
        onClose={() => { if (!deleting) setDelDialog({ open: false, id: null, name: "" }); }}
        maxWidth="xs" fullWidth>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{delDialog.name}</strong>?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDelDialog({ open: false, id: null, name: "" })}
            disabled={deleting} variant="outlined" size="small">
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}
            disabled={deleting} size="small"
            startIcon={deleting ? <CircularProgress size={14} /> : <DeleteIcon />}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}